import React from 'react';
import { Chip } from '@heroui/react';
import type { JourneyWithSteps, JourneyStep, TargetSelector, IntentRule } from '@/types';
import { SelectorReadOnlyView } from './SelectorReadOnlyView';

const JOURNEY_EVENT_LABELS: Record<string, string> = {
  pageview: 'Pageview',
  click: 'Click',
  impression: 'Impression',
  keypress: 'Keypress',
  drag_and_drop: 'Drag & Drop',
  key: 'Keypress',
  form_submit: 'Click',
};

function normalizeEventType(eventType?: string): string {
  if (!eventType) return 'Event';
  const normalized = eventType === 'key' ? 'keypress' : eventType === 'form_submit' ? 'click' : eventType;
  return JOURNEY_EVENT_LABELS[normalized] ?? normalized.replace(/_/g, ' ');
}

function targetToIntentRule(t: TargetSelector | undefined): IntentRule {
  if (!t) return {};
  return {
    target_type: t.target_type as IntentRule['target_type'],
    match_type: t.match_type,
    attribute_name: t.attribute_name,
    target_value: t.target_value ?? '',
  };
}

function StepTargetReadOnly({ step }: { step: JourneyStep }) {
  if (step.event_type === 'pageview') return null;

  if (step.event_type === 'keypress' || step.event_type === 'key') {
    const value = step.target_value || step.key || '';
    if (!value) return null;
    return (
      <div className="mt-2">
        <span className="text-default-500 text-xs font-medium">Key combination</span>
        <div className="mt-1 font-mono text-sm">{value}</div>
      </div>
    );
  }

  const hasIntent = step.target_type && (step.target_value != null && step.target_value !== '');
  const legacyValue = step.element_selector || (step.event_type !== 'keypress' && step.event_type !== 'key' ? step.target_value : null);

  if (hasIntent) {
    return (
      <div className="mt-2">
        <span className="text-default-500 text-xs font-medium">Target</span>
        <div className="mt-1">
          <SelectorReadOnlyView rule={targetToIntentRule(step as TargetSelector)} color="default" />
        </div>
      </div>
    );
  }

  if (legacyValue) {
    return (
      <div className="mt-2">
        <span className="text-default-500 text-xs font-medium">Target</span>
        <div className="mt-1">
          <SelectorReadOnlyView rule={{ css_selector: String(legacyValue) }} color="default" />
        </div>
      </div>
    );
  }

  return null;
}

interface JourneyReadOnlyViewProps {
  journey: JourneyWithSteps | null;
  className?: string;
}

export function JourneyReadOnlyView({ journey, className = '' }: JourneyReadOnlyViewProps) {
  if (!journey) {
    return (
      <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800">
        No journey data available.
      </p>
    );
  }

  const steps = journey.steps ?? [];
  const isOrdered = journey.is_ordered ?? true;

  return (
    <div className={`space-y-4 ${className}`}>
      {journey.description && (
        <p className="text-default-500 text-sm">{journey.description}</p>
      )}
      {isOrdered !== undefined && (
        <div className="flex items-center gap-2">
          <Chip size="sm" variant="flat" color={isOrdered ? 'primary' : 'default'}>
            {isOrdered ? 'Ordered' : 'Unordered'}
          </Chip>
        </div>
      )}
      <div className="space-y-3">
        <h4 className="text-default-700 text-sm font-semibold">Steps</h4>
        {steps.length === 0 ? (
          <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800">
            No steps defined.
          </p>
        ) : (
          steps.map((step, i) => (
            <div
              key={i}
              className="flex rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50"
            >
              <div className="mr-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-white bg-gray-400 text-sm font-semibold text-white dark:border-gray-700 dark:bg-gray-600">
                {isOrdered ? i + 1 : '•'}
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Chip size="sm" variant="flat" color="primary">
                    {normalizeEventType(step.event_type)}
                  </Chip>
                  {step.page_url && step.event_type !== 'pageview' && (
                    <span className="text-default-500 text-xs">on {step.page_url}</span>
                  )}
                </div>
                {step.event_type === 'pageview' && (
                  <div className="mt-2">
                    <span className="text-default-500 text-xs font-medium">Target URL/Path</span>
                    <div className="mt-1 font-mono text-sm">{step.page_url || '(empty)'}</div>
                  </div>
                )}
                {(step.event_type === 'click' || step.event_type === 'impression' || step.event_type === 'form_submit') && (
                  <StepTargetReadOnly step={step} />
                )}
                {(step.event_type === 'keypress' || step.event_type === 'key') && <StepTargetReadOnly step={step} />}
                {step.event_type === 'drag_and_drop' && (
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <div>
                      <span className="text-default-500 text-xs font-medium">Drag Source</span>
                      <div className="mt-1">
                        <SelectorReadOnlyView
                          rule={targetToIntentRule(step.drag_source)}
                          color="default"
                        />
                      </div>
                    </div>
                    <div>
                      <span className="text-default-500 text-xs font-medium">Drop Target</span>
                      <div className="mt-1">
                        <SelectorReadOnlyView
                          rule={targetToIntentRule(step.drop_target)}
                          color="default"
                        />
                      </div>
                    </div>
                    {step.page_url && (
                      <div className="md:col-span-2">
                        <span className="text-default-500 text-xs font-medium">URL condition</span>
                        <div className="mt-1 font-mono text-sm">{step.page_url}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
